/**
 * 第五階段（太魯閣包）：太魯閣峽谷和七星潭。
 * 標示「英文練習」的句子在所有語言中都刻意保留英文。
 * NPC 台詞裡的 “English practice:” 標記要保留原文：遊戲靠它找出要用英文朗讀的句子。
 */
import type { Messages } from './en';

export const zhTWTaroko: Pick<Messages, 'taroko'> = {
  taroko: {
    mapNames: { hualien: '太魯閣與七星潭' },
    zones: {
      shrine: '長春祠',
      buluowan: '布洛灣台地',
      shakadang: '砂卡礑步道',
      qingshui: '清水斷崖',
      village: '七星潭社區',
      visitor: '太魯閣遊客中心',
    },
    objects: {
      tk_visitor: '遊客中心',
      tk_shuttleTaroko: '台灣好行公車站（太魯閣）',
      tk_shuttleBeach: '台灣好行公車站（七星潭）',
      tk_cimu: '慈母橋涼亭',
      tk_rockSign: '小心落石告示牌',
      tk_swallows: '燕子口解說牌',
      tk_shrine: '長春祠',
      tk_bell: '鐘樓',
      tk_buluowan: '布洛灣告示牌',
      tk_weave: '太魯閣族織布機',
      tk_monkeys: '小心獼猴告示牌',
      tk_mochi: '小米麻糬攤',
      tk_shakadang: '砂卡礑步道指示牌',
      tk_bikeRent: '單車出租',
      tk_skip: '扁平的石頭',
      tk_sunrise: '曙光橋',
      tk_stargaze: '觀星平台',
      tk_museum: '七星柴魚博物館',
      tk_sausage: '飛魚香腸攤',
    },
    npc: {
      tk_guide: '導遊',
      tk_rangerB: '步道巡山員',
      tk_weaver: '太魯閣族織布師',
      tk_mochiAuntie: '太魯閣族阿姨',
      tk_fisher: '漁夫',
      tk_kid1: '打水漂的男孩',
      tk_kid2: '打水漂的女孩',
      tk_cyclist: '騎單車的人',
      tk_photographer: '攝影師',
      tk_bonito: '博物館導覽員',
      tk_stargazer: '觀星導覽員',
    },
    items: {},

    common: {
      firstStar: '⭐ 新發現！',
      notHere: '嗯，什麼事都沒發生。',
    },
    visitor: {
      text: '太魯閣國家公園遊客中心。一個大大的立體模型展示整座峽谷：立霧溪切出 19 公里長的大理石峭壁。',
      ask: '🗺️ 問路（英文）',
      q: '要怎麼有禮貌地問路呢？',
      // English practice
      a: ['Excuse me, how do I get to the Shakadang Trail?', 'Trail where? Tell me.', 'Shakadang now, go go go!'],
      right: '“Walk west along the river and cross the red bridge. The stairs go down to the trail!”',
      wrong: '志工笑著說：「換個更有禮貌的說法吧——用 ‘Excuse me…’ 開頭。」',
    },
    cimu: {
      text: '慈母橋：「慈愛母親的橋」。從前有一位母親，每天在這裡等她正在開山築路的兒子。這座橋就是為了紀念她的愛而命名的。',
      listen: '🌉 看看紅色的橋',
      looked: '兩隻白色的大理石獅子守護著橋。橋下的溪水又白又急。',
      // English practice
      phrase: 'Thank you, Mom! I love you.',
    },
    rock: {
      text: '一個黃色三角形，上面畫著掉下來的石頭。峭壁上隨時都可能有落石。',
      q: '這個告示牌是什麼意思？',
      // English practice
      a: ['Watch out for falling rocks!', 'Free rocks — take one!', 'A rock concert tonight.'],
      right: '沒錯！安全帽要戴好，不要在峭壁下停留。',
      wrong: '再看一次：石頭正從山坡上滾下來……',
    },
    swallows: {
      text: '燕子口：溪水在大理石上切出好幾百個小洞，燕子就在洞裡築巢！',
      count: '🐦 數燕子',
      q: (birds: string) => `${birds}\n\n你看到幾隻燕子？`,
      right: '數得好！燕子每年春天都會飛回這裡。',
      wrong: '再慢慢數一次：一、二、三……',
      // English practice: number words.
      numbers: ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'],
    },
    shrine: {
      text: '長春祠是為了紀念修築中部橫貫公路時犧牲的 226 位工作人員。祠下的泉水從來不會停止流動——所以叫做「長春」。',
      bow: '🙏 安靜地鞠躬',
      bowed: '你靜靜地站了一會兒。瀑布在紅色屋頂下轟隆作響。',
      // English practice
      phrase: '“Eternal” means “forever”.',
    },
    bell: {
      text: '陡峭階梯的最上面有一座鐘樓。從這裡可以看到整座峽谷！',
      ring: '🔔 敲鐘',
      rang: '噹……噹……鐘聲在峭壁之間迴盪。',
      // English practice
      phrase: 'I rang the bell. (ring → rang → rung)',
    },
    buluowan: {
      text: '布洛灣是太魯閣族家庭曾經居住和耕種的台地。在太魯閣族語裡，布洛灣的意思是「回音」。',
      shout: '📣 對著峽谷大喊 “Hello!”',
      // English practice
      hello: 'Hello!',
      echo: '“Hello… hello… hello…” 群山回答你了！',
    },
    weave: {
      text: '一台太魯閣族的水平背帶織布機。紅色的菱形圖紋叫做「祖靈之眼」——代表祖先守護著這個家，讓家人平安。',
      try: '🧵 學一個單字（英文）',
      q: '她在做什麼？',
      // English practice
      a: ['She is weaving cloth.', 'She is eating cloth.', 'She is sleeping on cloth.'],
      right: '對！“Weave” 就是「織」——她把紅線和白線織成布。',
      wrong: '看看她的手怎麼移動那些線……',
    },
    monkeys: {
      text: '台灣獼猴住在這些峭壁上。牠們是野生動物——而且對你的零食非常有興趣！',
      q: '一隻猴子盯著你的麻糬。你要怎麼做？',
      // English practice
      a: ['Don’t feed the monkeys. I keep my food in my bag.', 'I give it all my snacks!', 'I try to touch it.'],
      right: '對！人類的食物會讓野生猴子生病，還會讓牠們變得愛搶東西。',
      wrong: '嗯……猴子會咬人，而且我們的食物對牠們不好。再試一次！',
    },
    // English practice: food order.
    mochi: { vendor: 'Millet mochi, made with our Truku millet! Peanut or plain?', ok: 'Two peanut ones, please. How much are they?', bad: ['Mochi. Give.', 'I want everything, no money.'], done: '軟軟Q Q的，帶著小米的堅果香。阿姨揮揮手說：「步道走得開心喔！」', price: 30 },
    shakadang: {
      text: '砂卡礑步道：在清澈溪流上方的峭壁上開鑿出來的小路。“Skadang” 在太魯閣族語裡是「臼齒」的意思！',
      look: '💧 看看溪水',
      q: '溪水是什麼顏色？',
      // English practice
      a: ['It’s blue-green and very clear.', 'It’s brown and muddy.', 'It’s bright red.'],
      right: '對！連水底每一顆白色的大理石小石頭都看得到。',
      wrong: '仔細看看溪水……',
    },
    bike: {
      text: '單車出租：自行車道沿著整個七星潭海岸。',
      ride: '🚲 租一台單車（英文）',
      q: '要怎麼說呢？',
      // English practice
      a: ['I’d like to rent a bike for one hour, please.', 'Bike. One. Now.', 'Give me your bike.'],
      right: '你沿著海岸騎車，風吹過你的頭髮。左邊是山，右邊是太平洋！',
      wrong: '老闆笑著說：「可以說得再有禮貌一點嗎？」',
      price: 20,
      poor: '你的金幣不夠。',
    },
    skip: {
      text: '扁扁的、滑滑的小石頭——最適合在浪上打水漂了。',
      throw: '🥏 丟一顆石頭',
      again: '🥏 再試一次',
      result: (n: number, words: string) => (n === 0 ? '噗通！石頭直接沉下去了。' : `跳、跳……跳了 ${n} 下！${words}`),
      great: '太厲害了！小朋友都在幫你歡呼。',
    },
    sunrise: {
      text: '曙光橋。花蓮是台灣最早看到太陽從太平洋升起的地方之一。',
      early: '🌅 拍日出照片',
      photo: '📸 喀嚓！太陽從海面升起，橘色和金色的光好美。真是一張好照片！',
      late: '太陽已經升得很高了。早上 7 點以前再來拍日出吧！',
      // English practice
      phrase: 'The sun rises in the east.',
    },
    stars: {
      text: '觀星平台。七星潭的名字來自北斗七星的七顆星。',
      look: '✨ 看看天空',
      day: '現在是白天——星星都躲起來了。天黑以後再來吧！',
      q: '北斗七星有幾顆星？',
      // English practice
      a: ['Seven.', 'Three.', 'Twenty.'],
      right: '七顆！沿著「勺子」末端的兩顆星往前找，就能找到北極星。',
      wrong: '再數一次勺子形狀裡的星星……',
    },
    museum: {
      text: '七星柴魚博物館。七星潭以前是個漁村，漁夫會把鰹魚煙燻、曬乾，直到它硬得像木頭一樣。',
      learn: '🐟 學一個小知識',
      q: '為什麼鰹魚乾在中文裡叫做「柴魚」？',
      // English practice
      a: ['Because dried bonito is as hard as wood.', 'Because it lives in trees.', 'Because it is cooked on a campfire only.'],
      right: '答對了！再把它刨成薄薄的柴魚片，放在熱豆腐上會「跳舞」。',
      wrong: '提示：敲敲看柴魚——叩叩！',
    },
    // English practice: food order.
    sausage: { vendor: 'Flying-fish sausage! A Hualien speciality. With garlic?', ok: 'Yes, please. One sausage with garlic.', bad: ['Sausage! Fast!', 'Fish. Garlic. Mine.'], done: '有煙燻香味，多汁又帶點鹹，像大海的味道。游完泳吃最棒了！', price: 35 },

    talk: {
      ranger: {
        intro: '歡迎來到太魯閣！走峽谷步道之前，先回答一個安全問題。',
        q: '走峽谷步道時一定要戴什麼？',
        // English practice
        a: ['A helmet.', 'Flip-flops.', 'Sunglasses only.'],
        right: '很好！去安全帽租借處借一頂——免費的。',
        wrong: '想想看落石……',
      },
      tk_guide: [
        'Please follow me and stay together! This way, everyone! (English practice)',
        '在太魯閣族語裡，太魯閣的意思是「雄偉美麗」。',
        '我們的遊覽車三點出發。不要遲到喔！',
      ],
      tk_rangerB: [
        '請不要在峭壁下停留。繼續往前走。',
        '“Watch out!” 的意思是「小心，有東西過來了！」',
        '這裡的大理石在好幾百萬年前是海底。',
      ],
      tk_weaver: [
        '我的奶奶教我織布。紅色代表生命和勇氣。',
        'English practice: “What are you making?” — “I’m making a bag.”',
        '以前，太魯閣族的女孩在結婚前要先學會織布。',
      ],
      tk_mochiAuntie: [
        '小米在山上長得很好。我們用小米釀酒、做麻糬。',
        'English practice: “How much is it?” — “Thirty dollars.”',
      ],
      tk_fisher: [
        '我在日出前就出海了。早上的魚最餓！',
        'English practice: “Did you catch anything?” — “Yes, three fish!”',
        '這裡的海很快就變深。不要游泳——腳泡泡水就好。',
      ],
      tk_kid1: [
        '我的紀錄是跳七下！你能打破嗎？',
        '找一顆扁扁的石頭，壓低、快快地丟出去。像這樣！',
      ],
      tk_kid2: [
        'English practice: “Your turn!” — “Okay, here I go!”',
        '我們也會疊石頭。我的石塔有十顆那麼高！',
      ],
      tk_cyclist: [
        '“On your left!”——騎車超過別人的時候，英文就這樣說。',
        '自行車道可以一路騎到市區。有二十公里喔！',
      ],
      tk_photographer: [
        '清水斷崖有一千公尺高。我的相機根本拍不下全部！',
        'English practice: “Could you take a photo of us, please?”',
        '日出的時候來：海面會變成粉紅色和金色。',
      ],
      tk_bonito: [
        'English practice: “Would you like to try some?” — “Yes, please!”',
        '柴魚片要煙燻好多天。聞到了嗎？',
      ],
      tk_stargazer: [
        '抬頭看！那顆很亮的不是星星——是行星。',
        'English practice: “Can you see the Big Dipper?” — “Yes, I can!”',
        '遠離城市的燈光，在這裡可以看到銀河。',
      ],
    },
  },
};
